import { useQuery } from "@tanstack/react-query";
import { ConfigurationBlock, EditQuizStep } from "@verity/components";
import { Application, Extension, ProctoringOption } from "@verity/proctoring-option/types";
import { useContext } from "react";
import apiClient from "../../../../../../helpers/api-client";
import i18n from "../../../../../../i18n";
import { PortalControllerContext } from "../../../../../context";
import { getPlatformLabel } from "../../../utils";
import ContentWrapper from "../../components/ContentWrapper";
import { ConferencePlatformType, InterviewDetailsType } from "../../types";
import styles from "./ConfirmationPage.module.css";
import ConfirmationSkeleton from "./ConfirmationSkeleton";
import ConfirmationTable from "./components/ConfirmationTable";
import ConfirmationTableHeader from "./components/ConfirmationTableHeader";
import { formatDate, formatDuration, formatTime } from "./formatters";

type ConfirmationPageProps = {
	interviewDetails: InterviewDetailsType;
	conferencePlatform: ConferencePlatformType;
	proctoringOptions: Partial<ProctoringOption>;
	goToStep: (idx: number) => void;
};

export default function InterviewConfirmationPage({
	interviewDetails,
	conferencePlatform,
	proctoringOptions,
	goToStep,
}: ConfirmationPageProps) {
	const { verityInstituteSettings } = useContext(PortalControllerContext);

	const {
		data: applications,
		error: applicationsError,
		isLoading: applicationsLoading,
	} = useQuery<Application[]>({
		queryKey: ["applications"],
		queryFn: async () => await apiClient.verityOptions.getAllApplications(),
	});

	const {
		data: extensions,
		error: extensionsError,
		isLoading: extensionsLoading,
	} = useQuery<Extension[]>({
		queryKey: ["extensions"],
		queryFn: async () => await apiClient.verityOptions.getAllExtensions(),
	});

	if (applicationsLoading || extensionsLoading) return <ConfirmationSkeleton />;

	if (applicationsError || extensionsError) throw new Error(applicationsError?.message || extensionsError?.message);

	if (!applications || !extensions) return null;

	const configurationPages = ["record", "verification", "disableFeatures", "otherActivities", "showTools"];
	function handleGoToStep(pageName: EditQuizStep) {
		const CONFIG_OFFSET = 3;
		const index = configurationPages.indexOf(pageName);
		if (index >= 0) {
			goToStep(index + CONFIG_OFFSET);
		}
	}

	const InterviewDetailsData = [
		{
			label: i18n.t(`candidate.scheduleInterview.reviewStep.detailsValues.candidateName`),
			value: interviewDetails.candidateName,
		},
		{
			label: i18n.t(`candidate.scheduleInterview.reviewStep.detailsValues.candidateEmail`),
			value: interviewDetails.candidateEmail,
		},
		{
			label: i18n.t(`candidate.scheduleInterview.reviewStep.detailsValues.positionName`),
			value: interviewDetails.positionName,
		},
		{
			label: i18n.t(`candidate.scheduleInterview.reviewStep.detailsValues.dateAndTime`),
			value: `${formatDate(interviewDetails.startTime)}\t${formatTime(interviewDetails.startTime)}\t${formatDuration(interviewDetails.startTime, interviewDetails.endTime)}`,
		},
	];

	const VideoConferenceData = [
		{
			label: i18n.t(`candidate.scheduleInterview.reviewStep.platformValues.videoPlatform`),
			value: getPlatformLabel(conferencePlatform.videoPlatform),
		},
		{
			label: i18n.t(`candidate.scheduleInterview.reviewStep.platformValues.meetingLink`),
			value: conferencePlatform.meetingLink,
		},
		{
			label: i18n.t(`candidate.scheduleInterview.reviewStep.platformValues.notes`),
			value: conferencePlatform.notes || "None",
		},
	];

	return (
		<ContentWrapper
			title={i18n.t(`candidate.scheduleInterview.reviewStep.title`)}
			description={i18n.t(`candidate.scheduleInterview.reviewStep.description`)}
			className={styles.contentWrapper}
		>
			<div className={styles.configWrapper}>
				<div>
					<ConfirmationTableHeader
						title={i18n.t(`candidate.scheduleInterview.reviewStep.detailsTitle`)}
						onEdit={() => goToStep(0)}
					/>
					<ConfirmationTable data={InterviewDetailsData} />
				</div>
				<div>
					<ConfirmationTableHeader
						title={i18n.t(`candidate.scheduleInterview.reviewStep.platformTitle`)}
						onEdit={() => goToStep(1)}
					/>
					<ConfirmationTable data={VideoConferenceData} />
				</div>
				<div>
					<ConfirmationTableHeader
						title={i18n.t(`candidate.scheduleInterview.reviewStep.configurationsTitle`)}
						onEdit={() => goToStep(2)}
					/>
					<ConfigurationBlock
						onEdit={handleGoToStep}
						proctoringOption={proctoringOptions}
						verityInstituteSettings={verityInstituteSettings}
						applications={applications}
						extensions={extensions}
					/>
				</div>
			</div>
		</ContentWrapper>
	);
}
